import { useEffect, useState } from 'react';
import { Gavel, ExternalLink, RefreshCw } from 'lucide-react';
import supabase from '../lib/supabase';
import { PageHeader, Section, Badge, Button, EmptyState } from '../components/ui';

type Row = {
  id: string;
  stripe_dispute_id: string;
  amount: number;
  reason: string;
  status: string;
  resolution: string | null;
  created_at: string;
  updated_at: string;
  payments?: { stripe_charge_id: string | null; stripe_payment_intent_id: string | null; amount: number; currency: string } | null;
  bookings?: { booking_reference: string | null } | null;
};

const STATUS_TONE: Record<string, 'amber' | 'red' | 'green' | 'gray'> = {
  warning_needs_response: 'amber',
  warning_under_review:   'amber',
  warning_closed:         'gray',
  needs_response:         'red',
  under_review:           'amber',
  won:                    'green',
  lost:                   'red',
  charge_refunded:        'gray',
};

export default function Disputes() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('payment_disputes')
      .select(`
        id, stripe_dispute_id, amount, reason, status, resolution, created_at, updated_at,
        payments:payment_id(stripe_charge_id, stripe_payment_intent_id, amount, currency),
        bookings:booking_id(booking_reference)
      `)
      .order('created_at', { ascending: false })
      .limit(200);
    setRows(((data ?? []) as unknown as Row[]) || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const open    = rows.filter((r) => !['won', 'lost', 'charge_refunded', 'warning_closed'].includes(r.status));
  const closed  = rows.filter((r) => ['won', 'lost', 'charge_refunded', 'warning_closed'].includes(r.status));

  const totalOpen = open.reduce((s, r) => s + Number(r.amount ?? 0), 0);
  const currency = open[0]?.payments?.currency ?? 'AED';

  return (
    <div className="p-8 max-w-6xl">
      <PageHeader
        eyebrow="AceRent admin"
        title="Disputes"
        subtitle="Chargebacks and dispute evidence submissions from Stripe. Respond in the Stripe dashboard."
        actions={<Button variant="secondary" icon={RefreshCw} loading={loading} onClick={load}>Refresh</Button>}
      />

      <Section
        title={`Open (${open.length})`}
        subtitle={open.length > 0
          ? `${currency} ${Math.round(totalOpen).toLocaleString()} at risk across open disputes.`
          : 'Nothing needs a response right now.'}
        className="p-0 overflow-hidden"
      >
        {loading ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading disputes…</div>
        ) : open.length === 0 ? (
          <EmptyState
            icon={Gavel}
            title="No open disputes"
            description="When a customer disputes a charge, it lands here. Respond in Stripe and the status updates via webhook."
          />
        ) : (
          <DisputeTable rows={open} />
        )}
      </Section>

      {closed.length > 0 && (
        <Section
          title={`Resolved (${closed.length})`}
          subtitle="Disputes that have been won, lost, or refunded."
          className="p-0 overflow-hidden"
        >
          <DisputeTable rows={closed} />
        </Section>
      )}
    </div>
  );
}

function DisputeTable({ rows }: { rows: Row[] }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50/70 text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
        <tr>
          <th className="text-left px-5 py-3 font-semibold">Opened</th>
          <th className="text-left px-5 py-3 font-semibold">Amount</th>
          <th className="text-left px-5 py-3 font-semibold">Reason</th>
          <th className="text-left px-5 py-3 font-semibold">Booking</th>
          <th className="text-left px-5 py-3 font-semibold">Status</th>
          <th className="px-5 py-3"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50/60">
            <td className="px-5 py-3 whitespace-nowrap text-gray-700">
              {new Date(r.created_at).toLocaleString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric',
              })}
            </td>
            <td className="px-5 py-3 text-gray-900">
              {r.payments?.currency ?? 'AED'} {Number(r.amount).toFixed(0)}
            </td>
            <td className="px-5 py-3 capitalize text-gray-700">
              {r.reason?.replace(/_/g, ' ') ?? '—'}
            </td>
            <td className="px-5 py-3 font-mono text-[11px] text-gray-500">
              {r.bookings?.booking_reference ?? '—'}
            </td>
            <td className="px-5 py-3">
              <Badge tone={STATUS_TONE[r.status] ?? 'gray'}>
                {r.status.replace(/_/g, ' ')}
              </Badge>
              {r.resolution && (
                <div className="text-[11px] text-gray-500 mt-1">{r.resolution}</div>
              )}
            </td>
            <td className="px-5 py-3 text-right">
              <a
                href={`https://dashboard.stripe.com/payments/${r.payments?.stripe_payment_intent_id ?? ''}`}
                target="_blank" rel="noopener noreferrer"
                className="text-[11px] text-[#2d5016] hover:underline inline-flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" /> Stripe
              </a>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
